import { type ReactNode } from 'react'
import { isEmpty, type TeamData, type TeamGetSuccess } from '@terros/common'
import { showNotification } from '@mantine/notifications'
import { type UseFormReturnType } from '@mantine/form'
import { useToastError } from '@hooks'
import { usePreference, useRemoveTeam, useTranslation } from '@front/model'
import { DangerZone, PermissionTooltip, TButton } from '@components'

type Props = {
  form: UseFormReturnType<TeamData>
  teamData: TeamGetSuccess
}
export const TeamDangerZone = ({ teamData, form }: Props): ReactNode => {
  const { t } = useTranslation()
  const { setFieldValue, resetDirty } = form
  const { remove, isRemoving, error: removeError } = useRemoveTeam()
  useToastError(removeError)
  const { data: managerBasedTeams = false } = usePreference('managerBasedTeams', teamData.team.teamId)

  const { team } = teamData
  const childTeams = teamData.childTeams
  const members = teamData.members
  const canDelete = isEmpty(members) && isEmpty(childTeams)

  const handleTeamRemove = async (): Promise<void> => {
    await remove({ teamId: team.teamId, parentId: team.parentId })

    showNotification({
      title: t('Team Moved to Trash'),
      message: t('You can restore {{name}} anytime from the Trash.', { name: team.name }),
      color: 'green',
    })
    setFieldValue('isDeleted', true)
    resetDirty()
  }

  if (teamData.team.isDeleted) return

  return (
    <DangerZone>
      <PermissionTooltip
        label={t('You do not have permission to move a team to Trash')}
        permissionType='team:archive'
        targetId={team.teamId}
        extraConditions={[
          {
            active: !canDelete,
            label: t('You cannot move teams with sub-teams or users to Trash.'),
          },
          {
            active: managerBasedTeams,
            label: t('Teams are managed automatically and cannot be moved to Trash manually.'),
          },
        ]}
      >
        <TButton icon='delete' loading={isRemoving} variant='destructive' onClick={() => handleTeamRemove()}>
          {t('Move Team to Trash')}
        </TButton>
      </PermissionTooltip>
    </DangerZone>
  )
}
